import { Type } from "@sinclair/typebox";
const object = { additionalProperties: false };
const id = Type.String({ format: "uuid" });
const point = Type.Tuple([
    Type.Number({ minimum: -180, maximum: 180 }),
    Type.Number({ minimum: -90, maximum: 90 })
]);
export const aiManualAreaDefinitionSchema = Type.Union([
    Type.Object({
        kind: Type.Literal("radius"),
        center: point,
        radiusMeters: Type.Number({ minimum: 25, maximum: 25000 })
    }, object),
    Type.Object({
        kind: Type.Literal("polygon"),
        geometry: Type.Object({
            type: Type.Literal("Polygon"),
            coordinates: Type.Array(Type.Array(point, { minItems: 4, maxItems: 501 }), {
                minItems: 1,
                maxItems: 10
            })
        }, object)
    }, object)
]);
/** Canonical server-resolved geometry. This branch is deliberately excluded from manual requests. */
export const aiAreaMultiPolygonSchema = Type.Object({
    type: Type.Literal("MultiPolygon"),
    coordinates: Type.Array(Type.Array(Type.Array(point, { minItems: 4, maxItems: 100000 }), {
        minItems: 1,
        maxItems: 1000
    }), { minItems: 1, maxItems: 1000 })
}, object);
export const aiAreaIntentSchema = Type.Union([
    Type.Object({
        kind: Type.Literal("sector"),
        direction: Type.Union([
            Type.Literal("N"),
            Type.Literal("S"),
            Type.Literal("E"),
            Type.Literal("W"),
            Type.Literal("NE"),
            Type.Literal("NW"),
            Type.Literal("SE"),
            Type.Literal("SW")
        ])
    }, object),
    Type.Object({
        kind: Type.Literal("reference_radius"),
        query: Type.String({ minLength: 1, maxLength: 300 }),
        referenceType: Type.Union([
            Type.Literal("toll_booth"),
            Type.Literal("motorway_junction"),
            Type.Literal("place")
        ]),
        radiusMeters: Type.Union([Type.Number({ minimum: 25, maximum: 25000 }), Type.Null()])
    }, object),
    Type.Object({
        kind: Type.Literal("named_area"),
        query: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
        areaType: Type.Union([
            Type.Literal("industrial"),
            Type.Literal("productive"),
            Type.Literal("named")
        ])
    }, object),
    Type.Object({ kind: Type.Literal("unsupported"), reason: Type.String({ minLength: 1, maxLength: 1000 }) }, object)
]);
export const aiAreaSourceSchema = Type.Object({
    algorithmVersion: Type.Optional(Type.String()),
    origin: Type.Union([Type.Literal("sector"), Type.Literal("catalog"), Type.Literal("manual")]),
    provider: Type.String({ maxLength: 300 }),
    attribution: Type.String({ maxLength: 1000 }),
    snapshotAt: Type.Union([Type.String(), Type.Null()]),
    sourceUrl: Type.Union([Type.String(), Type.Null()]),
    license: Type.Union([Type.String(), Type.Null()]),
    sourceIds: Type.Array(Type.String(), { maxItems: 1000 }),
    snapshotSha256: Type.Union([Type.String(), Type.Null()]),
    geometryDigest: Type.String(),
    anchorMethod: Type.String(),
    limitations: Type.Array(Type.String(), { maxItems: 30 })
}, object);
export const aiAreaProposalCandidateSchema = Type.Object({
    id: Type.String({ minLength: 1, maxLength: 300 }),
    label: Type.String({ maxLength: 500 }),
    type: Type.Union([
        Type.Literal("sector"),
        Type.Literal("toll_booth"),
        Type.Literal("motorway_junction"),
        Type.Literal("place"),
        Type.Literal("industrial"),
        Type.Literal("productive"),
        Type.Literal("named")
    ]),
    geometry: Type.Union([aiAreaMultiPolygonSchema, Type.Null()]),
    anchor: Type.Union([point, Type.Null()]),
    source: aiAreaSourceSchema
}, object);
export const aiAreaDefinitionSchema = Type.Union([
    aiManualAreaDefinitionSchema,
    Type.Object({
        kind: Type.Literal("resolved"),
        proposalId: id,
        candidateId: Type.String(),
        radiusMeters: Type.Union([Type.Number(), Type.Null()]),
        geometry: aiAreaMultiPolygonSchema
    }, object)
]);
export const aiAreaProposalSchema = Type.Object({
    id,
    generation: Type.Integer(),
    areaIntentRevision: Type.Integer(),
    briefRevision: Type.Integer(),
    intentDigest: Type.String(),
    resolverVersion: Type.Optional(Type.String()),
    intent: aiAreaIntentSchema,
    state: Type.Union([
        Type.Literal("queued"),
        Type.Literal("running"),
        Type.Literal("ready"),
        Type.Literal("ambiguous"),
        Type.Literal("needs_radius"),
        Type.Literal("not_found"),
        Type.Literal("unavailable"),
        Type.Literal("unsupported"),
        Type.Literal("superseded")
    ]),
    message: Type.String(),
    reason: Type.Union([Type.String(), Type.Null()]),
    boundaryDigest: Type.String(),
    catalogReleaseId: Type.Union([Type.String(), Type.Null()]),
    candidates: Type.Array(aiAreaProposalCandidateSchema, { maxItems: 20 }),
    selectedCandidateId: Type.Union([Type.String(), Type.Null()]),
    radiusMeters: Type.Union([Type.Number(), Type.Null()]),
    limitToMunicipality: Type.Boolean(),
    previewId: Type.Union([id, Type.Null()]),
    createdAt: Type.String(),
    updatedAt: Type.String()
}, object);
export const aiAreaProposalRequestSchema = Type.Object({ idempotencyKey: id, expectedRevision: Type.Integer({ minimum: 1 }) }, object);
export const aiAreaProposalSelectSchema = Type.Object({
    idempotencyKey: id,
    expectedRevision: Type.Integer({ minimum: 1 }),
    proposalId: id,
    candidateId: Type.String({ minLength: 1, maxLength: 300 }),
    radiusMeters: Type.Union([Type.Number({ minimum: 25, maximum: 25000 }), Type.Null()]),
    limitToMunicipality: Type.Boolean()
}, object);
export const aiAreaRequestSchema = Type.Object({
    idempotencyKey: id,
    expectedRevision: Type.Integer({ minimum: 1 }),
    municipalityCode: Type.String({ pattern: "^\\d{6}$" }),
    label: Type.String({ minLength: 1, maxLength: 300 }),
    definition: aiManualAreaDefinitionSchema,
    limitToMunicipality: Type.Boolean()
}, object);
export const aiAreaPreviewSchema = Type.Object({
    id,
    status: Type.Union([
        Type.Literal("ready"),
        Type.Literal("unsupported_scope"),
        Type.Literal("not_found")
    ]),
    label: Type.String(),
    municipalityCode: Type.String(),
    definition: aiAreaDefinitionSchema,
    limitToMunicipality: Type.Boolean(),
    requestedGeometry: Type.Unknown(),
    effectiveGeometry: Type.Union([Type.Unknown(), Type.Null()]),
    requestedAreaM2: Type.Number(),
    effectiveAreaM2: Type.Union([Type.Number(), Type.Null()]),
    candidateCount: Type.Integer({ minimum: 0 }),
    anomalyCount: Type.Integer({ minimum: 0 }),
    boundaryDigest: Type.String(),
    queryDigest: Type.String(),
    briefRevision: Type.Integer(),
    areaIntentRevision: Type.Optional(Type.Integer({ minimum: 0 })),
    confirmedAreaId: Type.Union([id, Type.Null()]),
    source: Type.Optional(aiAreaSourceSchema)
}, object);
export const aiAreaConfirmSchema = Type.Object({ previewId: id, expectedRevision: Type.Integer({ minimum: 1 }) }, object);
export const aiTerritorialRunSchema = Type.Object({
    id,
    briefRevision: Type.Integer(),
    state: Type.Union([
        Type.Literal("queued"),
        Type.Literal("running"),
        Type.Literal("completed"),
        Type.Literal("needs_input"),
        Type.Literal("failed"),
        Type.Literal("superseded")
    ]),
    queryDigest: Type.String(),
    candidateCount: Type.Integer({ minimum: 0 }),
    error: Type.Union([Type.String(), Type.Null()]),
    updatedAt: Type.String()
}, object);
//# sourceMappingURL=ai-territory.js.map